import { LinePlusCircle } from '@qr-front/icons/line-plus-circle'
import { LineUser } from '@qr-front/icons/line-user'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { useAtomValue } from 'jotai'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { env, getContrastingNeutralColor } from '@/utils'

import {
	aboutAvatarImageAtom,
	aboutAvatarImageFileAtom,
	aboutFullNameAtom,
} from '../../../../contentFormSections'
import { qrUriAtom } from '../../../../generatorState'
import { TQrScreenProps } from '../../../../QrScreen/QrScreen'
import {
	vCardIsUnlockedAtom,
	vCardPrimaryColorAtom,
	vCardSecondaryColorAtom,
} from './VCardScreenState'

export const VCardHeader = ({ demoMode }: TQrScreenProps) => {
	const t = useTranslations()

	const primaryColor = useAtomValue(vCardPrimaryColorAtom)
	const primaryColorContrastingColor = getContrastingNeutralColor(primaryColor)
	const secondaryColor = useAtomValue(vCardSecondaryColorAtom)
	const secondaryColorContrastingColor =
		getContrastingNeutralColor(secondaryColor)
	const avatarImage = useAtomValue(aboutAvatarImageAtom)
	const avatarFile = useAtomValue(aboutAvatarImageFileAtom)
	const fullName = useAtomValue(aboutFullNameAtom)
	const qrUri = useAtomValue(qrUriAtom)
	const isUnlocked = useAtomValue(vCardIsUnlockedAtom)

	const [localPreviewSrc, setLocalPreviewSrc] = useState<string | null>(null)

	useEffect(() => {
		if (!avatarFile) {
			setLocalPreviewSrc(null)
			return
		}
		const url = URL.createObjectURL(avatarFile)
		setLocalPreviewSrc(url)
		return () => URL.revokeObjectURL(url)
	}, [avatarFile])

	const avatarSrc = avatarImage?.publicId
		? `${avatarImage.bucketRootUrl}/${avatarImage.publicId}`
		: localPreviewSrc

	return (
		<>
			<div
				className={cn('px-5 pb-6 pt-7 transition-colors word-break-fix')}
				style={{ backgroundColor: primaryColor }}
			>
				<div className="m-auto flex size-[6.25rem] items-center justify-center overflow-hidden rounded-full bg-white ring-4 ring-white">
					{demoMode && !avatarSrc ? (
						<div className="relative size-[100px]">
							<Image
								src="/web-app/qr-preview-demos/vcard/mock-avatar.webp"
								alt='alt="Contact image"'
								fill
								style={{ objectFit: 'cover' }}
								sizes="100px"
							/>
						</div>
					) : null}
					{avatarSrc ? (
						// eslint-disable-next-line @next/next/no-img-element
						<img
							src={avatarSrc}
							alt="Contact image"
							className="size-[100px] object-cover"
						/>
					) : (
						!demoMode && <LineUser className="text-dark-grey" />
					)}
				</div>
				<h4
					className="type-heading-4 mt-3 break-words text-center font-semibold transition-colors"
					style={{ color: primaryColorContrastingColor }}
				>
					{fullName ||
						getVersionForClone({
							base: 'Jane Cooper',
							clone1: 'Olivia Hayes',
						})}
				</h4>
				<a
					href={
						// Same-origin with the scan page, so the browser sends the unlock cookie
						isUnlocked
							? `/api-v1/common/qr-codes/vcard/${qrUri}`
							: `${env.API_URL}/common/qr-codes/vcard/${qrUri}`
					}
					className={cn(
						'type-body-caption mx-auto mt-2 flex h-10 w-fit items-center gap-2 rounded-full px-6 text-white transition-colors',
						!qrUri && 'pointer-events-none',
					)}
					style={{
						backgroundColor: secondaryColor,
						color: secondaryColorContrastingColor,
					}}
				>
					<LinePlusCircle className="size-4" />
					{t('generator__qr_content_screen__vcard_type__add_contact_button')}
				</a>
			</div>
			<div
				className="h-6 transition-colors"
				style={{ backgroundColor: primaryColor }}
			/>
		</>
	)
}
